import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "typeSelect",
    "messageFields",
    "inquiryFields",
    "cartAddFields",
    "cartComboAddFields",
    "cartModifyFields",
    "cartRemoveFields",
    "cartClearFields",
    "maxTurnsField",
    "cartFields",
    "textFields"
  ]

  static values = {
    menuCatalog: Object
  }

  connect() {
    this.buildMenuIndexes()

    if (this.hasTypeSelectTarget) {
      this.toggleFields()
    }

    this.initializeMenuRows(this.element)
  }

  toggleFields() {
    if (!this.hasTypeSelectTarget) return

    const selectedType = this.typeSelectTarget.value
    const fieldTargets = [
      "messageFields",
      "inquiryFields",
      "cartAddFields",
      "cartComboAddFields",
      "cartModifyFields",
      "cartRemoveFields",
      "cartClearFields",
      "cartFields",
      "textFields"
    ]

    fieldTargets.forEach(targetName => {
      const hasTarget = this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]
      if (!hasTarget) return

      const target = this[`${targetName}Target`]
      target.classList.add("hidden")
      target.querySelectorAll("[required]").forEach(field => {
        field.removeAttribute("required")
        field.dataset.wasRequired = "true"
      })
    })

    const typeToFieldMap = {
      message: "messageFields",
      inquiry: "inquiryFields",
      cart_add: "cartAddFields",
      cart_combo_add: "cartComboAddFields",
      cart_modify: "cartModifyFields",
      cart_remove: "cartRemoveFields",
      cart_clear: "cartClearFields",
      cart: "cartFields",
      text: "textFields"
    }

    const targetFieldName = typeToFieldMap[selectedType]
    if (targetFieldName) {
      this.showFieldsTarget(targetFieldName)
    }

    if (this.hasMaxTurnsFieldTarget) {
      this.maxTurnsFieldTarget.classList.toggle("hidden", selectedType === "message")
    }
  }

  handleNestedFieldAdded(event) {
    if (!event.detail?.wrapper) return

    this.initializeMenuRows(event.detail.wrapper)
  }

  refreshMenuFields(event) {
    const row = event.target.closest("[data-menu-row]")
    if (!row) return

    if (event.target.matches('[data-menu-field="item-select"]')) {
      const itemSelectedId = row.querySelector('[data-menu-field="item-selected-id"]')
      if (itemSelectedId) itemSelectedId.value = ""

      this.clearDependentSelectionState(row, { clearOption: true, clearAddons: true })
    } else if (event.target.matches('[data-menu-field="option-select"]')) {
      this.clearDependentSelectionState(row, { clearAddons: true })
    } else if (event.target.matches('[data-menu-field="addon-checkbox"]')) {
      this.syncAddonSelectFromCheckboxes(row)
      this.syncAddonNameInputs(
        row.querySelector('[data-menu-field="addon-name-inputs"]'),
        row.querySelector('[data-menu-field="addon-select"]')
      )
      return
    }

    if (row.dataset.menuRow === "combo") {
      this.syncComboRow(row)
      return
    }

    const comboRow = row.closest('[data-menu-row="combo"]')
    this.syncItemRow(row, comboRow ? this.allowedItemsForComboRow(comboRow) : this.catalogItems)
  }

  showFieldsTarget(targetName) {
    const hasTarget = this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]
    if (!hasTarget) return

    const target = this[`${targetName}Target`]
    target.classList.remove("hidden")
    target.querySelectorAll('[data-was-required="true"]').forEach(field => {
      field.setAttribute("required", "")
      delete field.dataset.wasRequired
    })
  }

  buildMenuIndexes() {
    this.catalogItems = this.arrayValue(this.menuCatalogValue?.items)
    this.catalogCombos = this.arrayValue(this.menuCatalogValue?.combos)
    this.itemsById = new Map(this.catalogItems.map(item => [String(item.id), item]))
    this.combosById = new Map(this.catalogCombos.map(combo => [String(combo.id), combo]))
  }

  initializeMenuRows(root) {
    this.menuRows(root, '[data-menu-row="item"], [data-menu-row="combo-item"]').forEach(row => {
      const comboRow = row.closest('[data-menu-row="combo"]')
      this.syncItemRow(row, comboRow ? this.allowedItemsForComboRow(comboRow) : this.catalogItems)
    })

    this.menuRows(root, '[data-menu-row="combo"]').forEach(row => {
      this.syncComboRow(row)
    })
  }

  menuRows(root, selector) {
    const rows = Array.from(root.querySelectorAll(selector))
    if (root.matches?.(selector)) {
      rows.unshift(root)
    }
    return rows
  }

  syncComboRow(row) {
    const comboSelect = row.querySelector('[data-menu-field="combo-select"]')
    const comboNameInput = row.querySelector('[data-menu-field="combo-name-input"]')
    const summary = row.querySelector('[data-menu-field="combo-groups-summary"]')
    if (!comboSelect) return

    const selectedComboId = this.resolveSelectedId(
      comboSelect,
      this.catalogCombos,
      comboSelect.dataset.selectedName
    )

    const activeComboId = this.renderSingleSelect(comboSelect, this.catalogCombos, {
      placeholder: "Select combo",
      selectedId: selectedComboId,
      selectedLabel: comboSelect.dataset.selectedName
    })

    const combo = this.combosById.get(String(activeComboId || ""))
    if (comboNameInput) {
      comboNameInput.value = combo?.name || this.selectedOptionLabel(comboSelect)
    }

    if (summary) {
      const groupSummary = this.arrayValue(combo?.item_groups).map(group => {
        const countText = group.allowed_items?.length ? `${group.allowed_items.length} choices` : "no mapped items"
        return `${group.name}: ${countText}`
      })

      summary.textContent = groupSummary.length ? groupSummary.join(" | ") : "Choose a combo to see its item groups."
    }

    const allowedItems = this.allowedItemsForComboRow(row)
    row.querySelectorAll('[data-menu-row="combo-item"]').forEach(comboItemRow => {
      this.syncItemRow(comboItemRow, allowedItems)
    })
  }

  syncItemRow(row, availableItems) {
    const itemSelect = row.querySelector('[data-menu-field="item-select"]')
    const optionSelect = row.querySelector('[data-menu-field="option-select"]')
    const addonSelect = row.querySelector('[data-menu-field="addon-select"]')
    const itemNameInput = row.querySelector('[data-menu-field="item-name-input"]')
    const optionNameInput = row.querySelector('[data-menu-field="option-name-input"]')
    const legacyOptionNameInput = row.querySelector('[data-menu-field="option-name-legacy-input"]')
    const addonNameInputs = row.querySelector('[data-menu-field="addon-name-inputs"]')

    if (!itemSelect || !optionSelect || !addonSelect) return

    const items = this.arrayValue(availableItems || this.catalogItems)
    const selectedItemId = this.resolveSelectedId(
      itemSelect,
      items,
      itemSelect.dataset.selectedName,
      row.querySelector('[data-menu-field="item-selected-id"]')?.value
    )

    const activeItemId = this.renderSingleSelect(itemSelect, items, {
      placeholder: row.dataset.menuRow === "combo-item" ? "Select combo item" : "Select menu item",
      selectedId: selectedItemId,
      selectedLabel: itemSelect.dataset.selectedName
    })

    const item = this.itemsById.get(String(activeItemId || ""))
    if (itemNameInput) {
      itemNameInput.value = item?.name || this.selectedOptionLabel(itemSelect)
    }

    const optionChoices = this.arrayValue(item?.options)
    const selectedOptionId = this.resolveOptionId(optionSelect, item)
    const activeOptionId = this.renderSingleSelect(optionSelect, optionChoices, {
      placeholder: "Select option",
      selectedId: selectedOptionId,
      selectedLabel: optionSelect.dataset.selectedName
    })

    const option = optionChoices.find(choice => String(choice.id) === String(activeOptionId || ""))
    if (optionNameInput) {
      optionNameInput.value = option?.name || this.selectedOptionLabel(optionSelect)
    }
    if (legacyOptionNameInput) {
      legacyOptionNameInput.value = option?.name || this.selectedOptionLabel(optionSelect)
    }

    const addonChoices = this.arrayValue(option?.addons || item?.addons)
    const selectedAddonIds = this.resolveAddonIds(addonSelect, addonChoices)
    this.renderMultiSelect(addonSelect, addonChoices, selectedAddonIds, {
      selectedLabels: this.selectedNames(addonSelect)
    })
    this.renderAddonCheckboxes(addonSelect)
    this.syncAddonNameInputs(addonNameInputs, addonSelect)
  }

  allowedItemsForComboRow(comboRow) {
    const comboSelect = comboRow.querySelector('[data-menu-field="combo-select"]')
    if (!comboSelect?.value) return this.catalogItems

    const combo = this.combosById.get(String(comboSelect.value))
    if (!combo) return this.catalogItems

    const allowedIds = this.arrayValue(combo.item_groups).flatMap(group => group.allowed_item_ids || [])
    const uniqueIds = Array.from(new Set(allowedIds.map(id => String(id))))
    if (!uniqueIds.length) return this.catalogItems

    return uniqueIds
      .map(id => this.itemsById.get(id))
      .filter(Boolean)
  }

  renderSingleSelect(select, options, { placeholder, selectedId, selectedLabel = "" }) {
    const normalizedSelectedId = selectedId ? String(selectedId) : ""
    const hasSelectedOption = options.some(option => String(option.id) === normalizedSelectedId)
    const optionHtml = [
      `<option value="">${placeholder}</option>`,
      ...options.map(option => {
        const optionId = String(option.id)
        const selected = optionId === normalizedSelectedId ? ' selected="selected"' : ""
        const label = this.escapeHtml(option.label || option.name || optionId)
        return `<option value="${this.escapeHtml(optionId)}"${selected}>${label}</option>`
      })
    ]

    if (normalizedSelectedId && !hasSelectedOption) {
      optionHtml.push(
        `<option value="${this.escapeHtml(normalizedSelectedId)}" selected="selected">${this.escapeHtml(selectedLabel || normalizedSelectedId)}</option>`
      )
    }

    select.innerHTML = optionHtml.join("")
    if (normalizedSelectedId) {
      select.value = normalizedSelectedId
    }
    return select.value
  }

  renderMultiSelect(select, options, selectedIds, { selectedLabels = [] } = {}) {
    const selectedSet = new Set(selectedIds.map(id => String(id)))
    const optionHtml = options.map(option => {
      const optionId = String(option.id)
      const selected = selectedSet.has(optionId) ? ' selected="selected"' : ""
      const label = this.escapeHtml(option.label || option.name || optionId)
      return `<option value="${this.escapeHtml(optionId)}"${selected}>${label}</option>`
    })

    const optionIds = new Set(options.map(option => String(option.id)))
    selectedIds.map(id => String(id)).forEach((selectedId, index) => {
      if (optionIds.has(selectedId)) return

      const label = selectedLabels[index] || selectedId
      optionHtml.push(`<option value="${this.escapeHtml(selectedId)}" selected="selected">${this.escapeHtml(label)}</option>`)
    })

    select.innerHTML = optionHtml.join("")
  }

  renderAddonCheckboxes(select) {
    const container = select.closest("[data-menu-row]")?.querySelector('[data-menu-field="addon-checkboxes"]')
    if (!container) return

    const options = Array.from(select.options || [])
    if (!options.length) {
      container.innerHTML = '<p class="px-2 py-1 text-sm text-slate-500">No add-ons available for this option.</p>'
      return
    }

    const nameKey = this.escapeHtml(select.name.replace(/\W+/g, "_"))
    container.innerHTML = options.map((option, index) => {
      const inputId = `${nameKey}_${this.escapeHtml(option.value || index)}`
      const checked = option.selected ? ' checked="checked"' : ""

      return `
        <label for="${inputId}" class="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-50">
          <input
            id="${inputId}"
            type="checkbox"
            value="${this.escapeHtml(option.value)}"
            class="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            data-menu-field="addon-checkbox"
            data-action="change->intent-form#refreshMenuFields"${checked}>
          <span>${this.escapeHtml(option.textContent.trim())}</span>
        </label>
      `
    }).join("")
  }

  syncAddonSelectFromCheckboxes(row) {
    const addonSelect = row.querySelector('[data-menu-field="addon-select"]')
    if (!addonSelect) return

    const checkedValues = new Set(
      Array.from(row.querySelectorAll('[data-menu-field="addon-checkbox"]:checked'))
        .map(checkbox => checkbox.value)
    )

    Array.from(addonSelect.options || []).forEach(option => {
      option.selected = checkedValues.has(option.value)
    })
  }

  resolveSelectedId(select, options, selectedName, explicitId = null) {
    const currentValue = select.value || explicitId || ""
    if (currentValue) return currentValue

    const normalizedSelectedName = this.normalizeText(selectedName)
    if (normalizedSelectedName) {
      const match = options.find(option => this.normalizeText(option.name || option.label) === normalizedSelectedName)
      if (match) return match.id
    }

    return null
  }

  resolveOptionId(optionSelect, item) {
    const currentValue = optionSelect.value || optionSelect
      .closest("[data-menu-row]")
      ?.querySelector('[data-menu-field="option-selected-id"]')
      ?.value || ""
    if (currentValue) return currentValue

    if (!item) return null

    const selectedName = this.normalizeText(optionSelect.dataset.selectedName)
    if (selectedName) {
      const match = this.arrayValue(item.options).find(option => this.normalizeText(option.name || option.label) === selectedName)
      if (match) return match.id
    }

    return item.default_option_id || item.options?.[0]?.id || null
  }

  resolveAddonIds(addonSelect, addonChoices) {
    const selectedFromDom = Array.from(addonSelect.selectedOptions || []).map(option => option.value).filter(Boolean)
    if (selectedFromDom.length) return selectedFromDom

    const selectedIds = addonSelect.dataset.selectedIds
      ? addonSelect.dataset.selectedIds.split(",").map(id => id.trim()).filter(Boolean)
      : []
    if (selectedIds.length) {
      return selectedIds
    }

    const selectedNames = addonSelect.dataset.selectedNames
      ? addonSelect.dataset.selectedNames.split("||").map(name => this.normalizeText(name))
      : []
    if (!selectedNames.length) return []

    return addonChoices
      .filter(choice => selectedNames.includes(this.normalizeText(choice.name || choice.label)))
      .map(choice => String(choice.id))
  }

  syncAddonNameInputs(container, addonSelect) {
    if (!container) return

    const hiddenName = addonSelect.name.replace("[addon_ids][]", "[addons][]")

    container.innerHTML = Array.from(addonSelect.selectedOptions || []).map(option => {
      const value = this.escapeHtml(option.textContent.trim())
      return `<input type="hidden" name="${this.escapeHtml(hiddenName)}" value="${value}">`
    }).join("")
  }

  clearDependentSelectionState(row, { clearOption = false, clearAddons = false } = {}) {
    if (clearOption) {
      const optionSelectedId = row.querySelector('[data-menu-field="option-selected-id"]')
      const optionNameInput = row.querySelector('[data-menu-field="option-name-input"]')
      const legacyOptionNameInput = row.querySelector('[data-menu-field="option-name-legacy-input"]')
      const optionSelect = row.querySelector('[data-menu-field="option-select"]')

      if (optionSelectedId) optionSelectedId.value = ""
      if (optionNameInput) optionNameInput.value = ""
      if (legacyOptionNameInput) legacyOptionNameInput.value = ""
      if (optionSelect) optionSelect.dataset.selectedName = ""
    }

    if (clearAddons) {
      const addonSelect = row.querySelector('[data-menu-field="addon-select"]')
      const addonNameInputs = row.querySelector('[data-menu-field="addon-name-inputs"]')

      if (addonSelect) {
        addonSelect.dataset.selectedIds = ""
        addonSelect.dataset.selectedNames = ""
      }
      if (addonNameInputs) {
        addonNameInputs.innerHTML = ""
      }
    }
  }

  normalizeText(value) {
    return String(value || "").trim().toLowerCase()
  }

  arrayValue(value) {
    return Array.isArray(value) ? value : []
  }

  selectedNames(select) {
    return select.dataset.selectedNames
      ? select.dataset.selectedNames.split("||").map(name => name.trim()).filter(Boolean)
      : []
  }

  selectedOptionLabel(select) {
    return Array.from(select.selectedOptions || [])
      .find(option => option.value)
      ?.textContent
      ?.trim() || ""
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
